<template>
  <NiceView
    :aboveTitle="generalStore.viewHeader"
    :title="CommonService.routes.PARTNERS.title"
    :icon="CommonService.routes.PARTNERS.icon"
    :loading="loading"
    flexBody
  >
    <!-- Filters -->
    <NiceFilters v-model="filtersList" @change="filterChanged" :showCreateButton="permissions.can_create_partners">
      <!-- More -->
      <NicePopup v-if="permissions.can_create_partners">
        <template #trigger>
          <NiceButton type="primary" icon="icon-more-vertical" />
        </template>
        <template #content>
          <div class="d-grid gap-2 text-left">
            <!-- Bulk invite links send -->
            <NiceButton icon="icon-send" :text="$gettext('Bulk send invites')" @click="openBulkInviteLinks"></NiceButton>

            <!-- Partner configuration -->
            <!-- <NiceButton icon="icon-settings" plain :text="$gettext('Configuration')" @click="openPartnerContent"></NiceButton> -->

            <!-- Export CSV -->
            <NiceButton icon="icon-download-cloud" :text="$gettext('Export CSV')" :loading="exportingFile" @click="exportCSV"></NiceButton>

            <!-- Download import template -->
            <NiceButton
              icon="icon-download-cloud"
              :text="$gettext('Download CSV template')"
              :loading="exportingFile"
              @click="downloadCsvTemplate"
            ></NiceButton>

            <!-- Import -->
            <NiceButton icon="icon-upload-cloud" :text="$gettext('Import')" @click="openImport"></NiceButton>
          </div>
        </template>
      </NicePopup>
    </NiceFilters>

    <!-- Table -->
    <NiceTable
      :actions="actions"
      :columns="columns"
      :data="data"
      :loading="loading"
      freezeLastColumn
      showFooter
      showLimit
      paginated
      @filterChange="filterChanged"
    />

    <!-- Form -->
    <NicePanel :name="panelName" @close="closeEdit">
      <PartnerForm @cancel="closeEdit" @success="successEdit" :data="selectedItem"></PartnerForm>
    </NicePanel>

    <!-- Partner content -->
    <!-- <NicePanel name="partner-content" @close="closeEdit">
      <PartnersContentForm @cancel="closeEdit" @success="successPartnerContentEdit"></PartnersContentForm>
    </NicePanel> -->

    <!-- Bulk send invoice links -->
    <BulkSendInviteLinks @success="successBulkInviteLinks"></BulkSendInviteLinks>

    <!-- Confirm send invite link -->
    <ConfirmModal
      name="send-invite-link"
      @confirm="confirmSendInviteLink"
      :loading="selectedPartner ? selectedPartner.loading : false"
      :title="$gettext('Send invite link')"
      :message="$gettext('Are you sure you want to send invite link to \'%{name}\'?', { name: selectedPartner ? selectedPartner.name : '' })"
    />

    <!-- Bulk send invoice links -->
    <ImportPartners @success="getData"></ImportPartners>

    <!-- <pre>{{ data }}</pre> -->
  </NiceView>
</template>

<script setup>
import { ref, inject, watch, onMounted } from "vue";

import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import ApiService from "@/services/api/api.service";

import ConfirmModal from "@/components/modals/ConfirmModal";
import PartnerForm from "@/components/forms/PartnerForm.vue";
import BulkSendInviteLinks from "@/components/modals/BulkSendInviteLinks.vue";
// import PartnersContentForm from "@/components/forms/PartnersContentForm.vue";
import ImportPartners from "@/components/modals/ImportPartners.vue";

import { formatDateWithTime } from "@/plugins/date";
const $getQuery = inject("getQuery");
const $nice = inject("nice");

import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();
import { useGeneralStore } from "@/store/general.store";

const generalStore = useGeneralStore();
const permissions = ref(generalStore.permissions);

const data = ref([]);
const loading = ref(false);
const exportingFile = ref(false);
const selectedItem = ref(null);
const selectedPartner = ref(null);
const panelName = "partner";
const organizationId = router.currentRoute.value.params.organizationId;
const eventId = router.currentRoute.value.params.eventId;

// Watch for id change
watch(
  () => route.query,
  () => {
    if (route.query.id) {
      $nice.panel(panelName, true);
    }
  }
);

onMounted(async () => {
  if (route.query.id) {
    $nice.panel(panelName, true);
  }
});

function closeEdit() {
  selectedItem.value = null;
  router.push({ query: { ...route.query, id: undefined } });
  $nice.panel(panelName, false);
}

function successEdit() {
  closeEdit();
  getData();
}

function openEdit(item) {
  selectedItem.value = item;
  router.push({ query: { ...route.query, id: item.id } });
}

function openBulkInviteLinks() {
  $nice.modal("bulk-send-invite-links", true);
}

function successBulkInviteLinks() {
  $nice.modal("bulk-send-invite-links", false);
  getData();
}

// function openPartnerContent() {
//   $nice.panel("partner-content", true);
// }

// function successPartnerContentEdit() {
//   $nice.panel("partner-content", false);
// }

function copyInviteLink(partner) {
  CommonService.copyTextToClipboard(CommonService.generateInviteLink(partner.invite_link_uuid));
}

async function sendInviteLink(partner) {
  selectedPartner.value = partner;
  $nice.modal("send-invite-link", true);
}

async function confirmSendInviteLink() {
  selectedPartner.value.loading = true;
  try {
    await ApiService.partners.sendInviteLink(organizationId, eventId, selectedPartner.value.id);
    selectedPartner.value.is_invite_link_email_sent = true;
    selectedPartner.value = false;
    getData();
    $nice.modal("send-invite-link", false);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    selectedPartner.value = false;
  }
}

function openImport() {
  $nice.modal("import-partners", true);
}

async function downloadCsvTemplate() {
  exportingFile.value = true;
  try {
    const response = await ApiService.partners.downloadImportTemplate(route.params.organizationId, route.params.eventId);
    CommonService.downloadBlob(response, "partners-import-template.csv");
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    exportingFile.value = false;
  }
}

async function exportCSV() {
  exportingFile.value = true;
  try {
    const response = await ApiService.partners.export(organizationId, eventId);
    const filename = `Partners-${generalStore.event.name}-${generalStore.organization.name}.csv`;
    CommonService.downloadFile(response, filename);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    exportingFile.value = false;
  }
}

async function getData() {
  loading.value = true;
  try {
    const query = await $getQuery();
    data.value = await ApiService.partners.getAll(organizationId, eventId, query);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    loading.value = false;
  }
}

const filtersList = [
  {
    name: $gettext("Invite link sent"),
    type: "yesno",
    key: "is_invite_link_email_sent",
  },
  {
    name: $gettext("Guest limit reached"),
    type: "yesno",
    key: "guest_limit_reached",
  },
];

function filterChanged() {
  getData();
}

const actions = [
  {
    icon: () => "icon-link",
    title: () => $gettext("Copy invite link"),
    function: (v) => copyInviteLink(v),
    hidden: (v) => !permissions.value.can_edit_partners,
  },
  {
    icon: () => "icon-send",
    title: () => $gettext("Send invite link"),
    function: (v) => sendInviteLink(v),
    hidden: (v) => !permissions.value.can_edit_partners,
  },
  {
    icon: () => "icon-edit-3",
    title: () => $gettext("Edit"),
    function: (v) => openEdit(v),
    hidden: () => !permissions.value.can_edit_partners,
  },
];

const columns = [
  // {
  //   key: "indicator",
  //   class: "w-0 pr-1",
  //   fieldClass: (v, row) => {
  //     return row.active ? "nice-indicator active" : "nice-indicator";
  //   },
  // },
  {
    name: $gettext("Name"),
    key: "name",
    hasOrder: true,
  },
  {
    name: $gettext("Contact name"),
    key: "contact_name",
    hasOrder: true,
  },
  {
    name: $gettext("Contact email"),
    key: "contact_email",
    hasOrder: true,
  },
  {
    name: $gettext("Active guests"),
    key: "guest_active_count",
    class: "text-right",
    formatter: (v, row) => v + " / " + row.invite_link_guest_limit,
    hasOrder: true,
  },
  {
    name: $gettext("Checked in guests"),
    key: "guest_checked_in_count",
    class: "text-right",
    formatter: (v, row) => v + " / " + row.guest_active_count,
    hasOrder: true,
  },
  {
    name: $gettext("Deactivated guests"),
    key: "guest_deactivated_count",
    class: "text-right",
    hasOrder: true,
  },
  {
    name: $gettext("Guest group"),
    key: "guest_group",
    class: "text-center",
    html: (v) => `<span class="badge" style="background-color: ${v.color};">${v.name}</span>`,
    hasOrder: true,
  },
  // {
  //   name: $gettext("Invite link"),
  //   key: "invite_link_uuid",
  //   html: (v) => `<a target="blank" href="${CommonService.generateInviteLink(v)}">${$gettext("Link")}</a>`,
  // },
  {
    name: $gettext("Invite link sent"),
    key: "is_invite_link_email_sent",
    formatter: (v) => (v ? $gettext("Yes") : $gettext("No")),
    hasOrder: true,
  },
  {
    name: $gettext("Invite link expire at"),
    key: "invite_link_expire_at",
    class: "text-right",
    formatter: (v) => formatDateWithTime(v),
    hasOrder: true,
  },
  // {
  //   name: $gettext("Created"),
  //   key: "created",
  //   class: "text-right",
  //   formatter: (v) => formatDateWithTime(v),
  // },
];
</script>
